'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Users,
  Shield,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Phone,
  Mail,
  Copy,
  Check,
  Circle,
  ArrowRight,
  UserCheck,
  CalendarDays,
  Lock,
  TrendingUp,
  CheckCircle2,
  Timer,
  Activity,
  Zap,
  Download,
  FileText,
  Send,
  X,
  Calendar,
} from 'lucide-react';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string;
  name: string;
  role: string;
  department: string;
  email: string;
  password: string;
  phone: string;
  status: 'online' | 'busy' | 'away' | 'offline';
  location: string;
  avatar: string;
  onCallShift?: string;
}

interface EscalationRule {
  id: string;
  name: string;
  trigger: string;
  level: number;
  assignee: string;
  waitMinutes: number;
  channel: string;
}

interface OnCallSchedule {
  day: string;
  primary: string;
  secondary: string;
  shift: string;
}

interface MemberKPI {
  memberId: string;
  wosCompleted: number;
  avgResolutionHrs: number;
  slaCompliance: number;
  capacityUtilization: number;
  trend: 'up' | 'down' | 'stable';
}

interface MonthlyTrend {
  month: string;
  [key: string]: string | number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const teamMembers: TeamMember[] = [
  {
    id: 'tm-1',
    name: 'Dawit Taddese',
    role: 'Network Engineer',
    department: 'Network',
    email: 'dawit.taddese@ai.nablix.net',
    password: 'NetOps@2024',
    phone: '+251 91 234 5678',
    status: 'online',
    location: 'Bole, Addis Ababa',
    avatar: 'DT',
    onCallShift: 'Mon–Wed 08:00–20:00',
  },
  {
    id: 'tm-2',
    name: 'Selam Bekele',
    role: 'NOC Analyst',
    department: 'Network',
    email: 'selam.bekele@ai.nablix.net',
    password: 'NOC@Selam25',
    phone: '+251 92 345 6789',
    status: 'online',
    location: 'Yeka, Addis Ababa',
    avatar: 'SB',
    onCallShift: 'Thu–Sat 08:00–20:00',
  },
  {
    id: 'tm-3',
    name: 'Yonas Girma',
    role: 'Systems Administrator',
    department: 'Infrastructure',
    email: 'yonas.girma@ai.nablix.net',
    password: 'SysAdmin@Yonas24',
    phone: '+251 93 456 7890',
    status: 'busy',
    location: 'Arada, Addis Ababa',
    avatar: 'YG',
    onCallShift: 'Mon–Fri 09:00–18:00',
  },
  {
    id: 'tm-4',
    name: 'Hiwot Alemu',
    role: 'Security Analyst',
    department: 'Security',
    email: 'hiwot.alemu@ai.nablix.net',
    password: 'SecOps@Hiwot25',
    phone: '+251 94 567 8901',
    status: 'online',
    location: 'Kirkos, Addis Ababa',
    avatar: 'HA',
    onCallShift: 'Tue–Thu 20:00–08:00',
  },
  {
    id: 'tm-5',
    name: 'Bereket Haile',
    role: 'DevOps Engineer',
    department: 'Infrastructure',
    email: 'bereket.haile@ai.nablix.net',
    password: 'DevOps@Bereket24',
    phone: '+251 95 678 9012',
    status: 'away',
    location: 'Lideta, Addis Ababa',
    avatar: 'BH',
    onCallShift: 'Fri–Sun 08:00–20:00',
  },
  {
    id: 'tm-6',
    name: 'Tigist Worku',
    role: 'Network Operations Manager',
    department: 'Management',
    email: 'tigist.worku@ai.nablix.net',
    password: 'Manager@Tigist25',
    phone: '+251 96 789 0123',
    status: 'online',
    location: 'Kolfe Keranio, Addis Ababa',
    avatar: 'TW',
    onCallShift: 'On-call 24/7',
  },
  {
    id: 'tm-7',
    name: 'Abebe Mekonen',
    role: 'Incident Responder',
    department: 'Network',
    email: 'abebe.mekonen@ai.nablix.net',
    password: 'Incident@Abebe24',
    phone: '+251 97 890 1234',
    status: 'offline',
    location: 'Akaki Kality, Addis Ababa',
    avatar: 'AM',
    onCallShift: 'Sun–Mon 20:00–08:00',
  },
];

const departmentRoles: { dept: string; color: string; roles: string[] }[] = [
  {
    dept: 'Network',
    color: 'text-primary bg-primary/10 border-primary/20',
    roles: ['Network Engineer', 'NOC Analyst', 'Incident Responder'],
  },
  {
    dept: 'Infrastructure',
    color: 'text-success bg-success/10 border-success/20',
    roles: ['Systems Administrator', 'DevOps Engineer'],
  },
  {
    dept: 'Security',
    color: 'text-warning bg-warning/10 border-warning/20',
    roles: ['Security Analyst', 'Threat Intelligence'],
  },
  {
    dept: 'Management',
    color: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
    roles: ['Network Operations Manager', 'Team Lead'],
  },
];

const onCallSchedule: OnCallSchedule[] = [
  { day: 'Monday', primary: 'Dawit Taddese', secondary: 'Selam Bekele', shift: '08:00 – 20:00' },
  { day: 'Tuesday', primary: 'Dawit Taddese', secondary: 'Hiwot Alemu', shift: '08:00 – 20:00' },
  { day: 'Wednesday', primary: 'Dawit Taddese', secondary: 'Yonas Girma', shift: '08:00 – 20:00' },
  { day: 'Thursday', primary: 'Selam Bekele', secondary: 'Hiwot Alemu', shift: '08:00 – 20:00' },
  { day: 'Friday', primary: 'Selam Bekele', secondary: 'Bereket Haile', shift: '08:00 – 20:00' },
  { day: 'Saturday', primary: 'Selam Bekele', secondary: 'Abebe Mekonen', shift: '08:00 – 20:00' },
  { day: 'Sunday', primary: 'Abebe Mekonen', secondary: 'Bereket Haile', shift: '08:00 – 20:00' },
];

const nightShift: OnCallSchedule[] = [
  { day: 'Monday', primary: 'Abebe Mekonen', secondary: 'Hiwot Alemu', shift: '20:00 – 08:00' },
  { day: 'Tuesday', primary: 'Hiwot Alemu', secondary: 'Abebe Mekonen', shift: '20:00 – 08:00' },
  { day: 'Wednesday', primary: 'Hiwot Alemu', secondary: 'Yonas Girma', shift: '20:00 – 08:00' },
  { day: 'Thursday', primary: 'Abebe Mekonen', secondary: 'Hiwot Alemu', shift: '20:00 – 08:00' },
  { day: 'Friday', primary: 'Bereket Haile', secondary: 'Abebe Mekonen', shift: '20:00 – 08:00' },
  { day: 'Saturday', primary: 'Yonas Girma', secondary: 'Bereket Haile', shift: '20:00 – 08:00' },
  { day: 'Sunday', primary: 'Hiwot Alemu', secondary: 'Yonas Girma', shift: '20:00 – 08:00' },
];

const escalationRules: EscalationRule[] = [
  {
    id: 'esc-1',
    name: 'Critical Network Outage',
    trigger: 'Severity: Critical · Health < 40%',
    level: 1,
    assignee: 'Dawit Taddese',
    waitMinutes: 5,
    channel: 'SMS + Call',
  },
  {
    id: 'esc-2',
    name: 'Critical Network Outage',
    trigger: 'Severity: Critical · Health < 40%',
    level: 2,
    assignee: 'Tigist Worku',
    waitMinutes: 10,
    channel: 'Call + Email',
  },
  {
    id: 'esc-3',
    name: 'High Latency Alert',
    trigger: 'Latency > 200ms · Duration > 5min',
    level: 1,
    assignee: 'Selam Bekele',
    waitMinutes: 10,
    channel: 'Slack + Email',
  },
  {
    id: 'esc-4',
    name: 'High Latency Alert',
    trigger: 'Latency > 200ms · Duration > 5min',
    level: 2,
    assignee: 'Dawit Taddese',
    waitMinutes: 20,
    channel: 'SMS + Call',
  },
  {
    id: 'esc-5',
    name: 'Security Breach Detected',
    trigger: 'IDS Alert · Severity ≥ High',
    level: 1,
    assignee: 'Hiwot Alemu',
    waitMinutes: 3,
    channel: 'Call + SMS',
  },
  {
    id: 'esc-6',
    name: 'Security Breach Detected',
    trigger: 'IDS Alert · Severity ≥ High',
    level: 2,
    assignee: 'Tigist Worku',
    waitMinutes: 8,
    channel: 'Call + Email',
  },
  {
    id: 'esc-7',
    name: 'Node Down',
    trigger: 'Node unreachable > 2min',
    level: 1,
    assignee: 'Yonas Girma',
    waitMinutes: 5,
    channel: 'Slack',
  },
  {
    id: 'esc-8',
    name: 'Node Down',
    trigger: 'Node unreachable > 2min',
    level: 2,
    assignee: 'Dawit Taddese',
    waitMinutes: 15,
    channel: 'SMS + Call',
  },
];

// ─── Performance KPI Data ─────────────────────────────────────────────────────

const memberKPIs: MemberKPI[] = [
  { memberId: 'tm-1', wosCompleted: 47, avgResolutionHrs: 2.4, slaCompliance: 94, capacityUtilization: 82, trend: 'up' },
  { memberId: 'tm-2', wosCompleted: 38, avgResolutionHrs: 3.1, slaCompliance: 89, capacityUtilization: 71, trend: 'up' },
  { memberId: 'tm-3', wosCompleted: 29, avgResolutionHrs: 4.7, slaCompliance: 85, capacityUtilization: 68, trend: 'stable' },
  { memberId: 'tm-4', wosCompleted: 22, avgResolutionHrs: 5.2, slaCompliance: 91, capacityUtilization: 59, trend: 'up' },
  { memberId: 'tm-5', wosCompleted: 33, avgResolutionHrs: 3.8, slaCompliance: 87, capacityUtilization: 74, trend: 'down' },
  { memberId: 'tm-6', wosCompleted: 14, avgResolutionHrs: 1.9, slaCompliance: 97, capacityUtilization: 45, trend: 'stable' },
  { memberId: 'tm-7', wosCompleted: 41, avgResolutionHrs: 2.8, slaCompliance: 88, capacityUtilization: 78, trend: 'down' },
];

const monthlyTrends: MonthlyTrend[] = [
  { month: 'Sep', DT: 38, SB: 30, YG: 22, HA: 17, BH: 27, TW: 11, AM: 33 },
  { month: 'Oct', DT: 41, SB: 33, YG: 24, HA: 19, BH: 29, TW: 12, AM: 36 },
  { month: 'Nov', DT: 43, SB: 35, YG: 26, HA: 20, BH: 31, TW: 13, AM: 38 },
  { month: 'Dec', DT: 40, SB: 32, YG: 23, HA: 18, BH: 28, TW: 11, AM: 35 },
  { month: 'Jan', DT: 44, SB: 36, YG: 27, HA: 21, BH: 30, TW: 13, AM: 39 },
  { month: 'Feb', DT: 45, SB: 37, YG: 28, HA: 21, BH: 32, TW: 14, AM: 40 },
  { month: 'Mar', DT: 47, SB: 38, YG: 29, HA: 22, BH: 33, TW: 14, AM: 41 },
];

const slaMonthlyTrends: MonthlyTrend[] = [
  { month: 'Sep', DT: 90, SB: 85, YG: 81, HA: 87, BH: 83, TW: 95, AM: 84 },
  { month: 'Oct', DT: 91, SB: 86, YG: 82, HA: 88, BH: 84, TW: 96, AM: 85 },
  { month: 'Nov', DT: 92, SB: 87, YG: 83, HA: 89, BH: 85, TW: 96, AM: 86 },
  { month: 'Dec', DT: 90, SB: 85, YG: 81, HA: 87, BH: 83, TW: 95, AM: 84 },
  { month: 'Jan', DT: 93, SB: 88, YG: 84, HA: 90, BH: 86, TW: 97, AM: 87 },
  { month: 'Feb', DT: 93, SB: 88, YG: 84, HA: 90, BH: 87, TW: 97, AM: 87 },
  { month: 'Mar', DT: 94, SB: 89, YG: 85, HA: 91, BH: 87, TW: 97, AM: 88 },
];

const MEMBER_COLORS: Record<string, string> = {
  DT: '#22d3ee',
  SB: '#34d399',
  YG: '#fb923c',
  HA: '#f472b6',
  BH: '#a78bfa',
  TW: '#facc15',
  AM: '#60a5fa',
};

// ─── SLA Trends by Node Type & Customer Segment ───────────────────────────────

type NodeTypeFilter = 'all' | 'BN' | 'RN';
type SegmentFilter = 'all' | 'MDU' | 'business' | 'single-dwelling';

interface SlaTrendPoint {
  month: string;
  BN_MDU: number;
  BN_business: number;
  BN_single: number;
  RN_MDU: number;
  RN_business: number;
  RN_single: number;
}

const slaTrendsBySegment: SlaTrendPoint[] = [
  { month: 'Sep', BN_MDU: 92, BN_business: 94, BN_single: 96, RN_MDU: 84, RN_business: 87, RN_single: 90 },
  { month: 'Oct', BN_MDU: 91, BN_business: 93, BN_single: 95, RN_MDU: 83, RN_business: 86, RN_single: 89 },
  { month: 'Nov', BN_MDU: 93, BN_business: 95, BN_single: 97, RN_MDU: 85, RN_business: 88, RN_single: 91 },
  { month: 'Dec', BN_MDU: 90, BN_business: 92, BN_single: 94, RN_MDU: 82, RN_business: 85, RN_single: 88 },
  { month: 'Jan', BN_MDU: 94, BN_business: 96, BN_single: 97, RN_MDU: 86, RN_business: 89, RN_single: 92 },
  { month: 'Feb', BN_MDU: 95, BN_business: 96, BN_single: 98, RN_MDU: 87, RN_business: 90, RN_single: 93 },
  { month: 'Mar', BN_MDU: 96, BN_business: 97, BN_single: 98, RN_MDU: 88, RN_business: 91, RN_single: 94 },
];

const SLA_SERIES: {
  key: keyof Omit<SlaTrendPoint, 'month'>;
  label: string;
  nodeType: 'BN' | 'RN';
  segment: 'MDU' | 'business' | 'single-dwelling';
  color: string;
}[] = [
  { key: 'BN_MDU',      label: 'BN · MDU',            nodeType: 'BN', segment: 'MDU',              color: '#22d3ee' },
  { key: 'BN_business', label: 'BN · Business',        nodeType: 'BN', segment: 'business',         color: '#34d399' },
  { key: 'BN_single',   label: 'BN · Single-Dwelling', nodeType: 'BN', segment: 'single-dwelling',  color: '#60a5fa' },
  { key: 'RN_MDU',      label: 'RN · MDU',             nodeType: 'RN', segment: 'MDU',              color: '#f472b6' },
  { key: 'RN_business', label: 'RN · Business',        nodeType: 'RN', segment: 'business',         color: '#fb923c' },
  { key: 'RN_single',   label: 'RN · Single-Dwelling', nodeType: 'RN', segment: 'single-dwelling',  color: '#a78bfa' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const statusDot: Record<string, string> = {
  online: 'bg-success',
  busy: 'bg-danger',
  away: 'bg-warning',
  offline: 'bg-muted',
};

const statusLabel: Record<string, string> = {
  online: 'Online',
  busy: 'Busy',
  away: 'Away',
  offline: 'Offline',
};

const deptColor: Record<string, string> = {
  Network: 'text-primary bg-primary/10 border-primary/20',
  Infrastructure: 'text-success bg-success/10 border-success/20',
  Security: 'text-warning bg-warning/10 border-warning/20',
  Management: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
};

function CredentialRow({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-[11px] text-muted w-20 flex-shrink-0">{label}</span>
      <span className={`text-[11px] text-foreground-muted flex-1 truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
      <button onClick={copy} className="text-muted hover:text-primary transition-colors flex-shrink-0">
        {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function MemberCard({ member }: { member: TeamMember }) {
  const [showCreds, setShowCreds] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-primary/30 transition-all duration-150">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
            {member.avatar}
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface ${statusDot[member.status]}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground truncate">{member.name}</p>
          <p className="text-[11px] text-muted truncate">{member.role}</p>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${deptColor[member.department] || 'text-muted bg-surface-raised border-border'}`}>
          {member.department}
        </span>
      </div>

      {/* Info rows */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <Mail size={11} className="flex-shrink-0" />
          <span className="truncate font-mono">{member.email}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <Phone size={11} className="flex-shrink-0" />
          <span>{member.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <Clock size={11} className="flex-shrink-0" />
          <span>{member.onCallShift}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <Circle size={11} className={`flex-shrink-0 fill-current ${statusDot[member.status]}`} />
          <span>{statusLabel[member.status]} · {member.location}</span>
        </div>
      </div>

      {/* Credentials toggle */}
      <button
        onClick={() => setShowCreds(!showCreds)}
        className="flex items-center gap-1.5 text-[11px] text-primary hover:text-primary/80 font-medium transition-colors w-full"
      >
        <Lock size={11} />
        Login Credentials
        {showCreds ? <ChevronUp size={11} className="ml-auto" /> : <ChevronDown size={11} className="ml-auto" />}
      </button>

      {showCreds && (
        <div className="mt-2 pt-2 border-t border-border space-y-0.5">
          <CredentialRow label="Email" value={member.email} />
          <div className="flex items-center justify-between gap-2 py-1">
            <span className="text-[11px] text-muted w-20 flex-shrink-0">Password</span>
            <span className="text-[11px] font-mono text-foreground-muted flex-1 truncate">
              {showPwd ? member.password : '••••••••••••'}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => setShowPwd(!showPwd)} className="text-muted hover:text-primary transition-colors">
                {showPwd ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(member.password); }}
                className="text-muted hover:text-primary transition-colors"
              >
                <Copy size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Export / Email Utilities ─────────────────────────────────────────────────

function exportCSV(kpis: MemberKPI[], members: TeamMember[], month: string) {
  const headers = ['Name', 'Role', 'Department', 'Email', 'WOs Completed', 'Avg Resolution (hrs)', 'SLA Compliance (%)', 'Capacity Utilization (%)', 'Trend', 'Period'];
  const rows = kpis.map((k) => {
    const m = members.find((tm) => tm.id === k.memberId);
    return [
      m?.name || '',
      m?.role || '',
      m?.department || '',
      m?.email || '',
      k.wosCompleted,
      k.avgResolutionHrs,
      k.slaCompliance,
      k.capacityUtilization,
      k.trend,
      month,
    ];
  });

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `team-performance-${month.replace(' ', '-').toLowerCase()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function generatePDFReport(
  kpis: MemberKPI[],
  members: TeamMember[],
  month: string,
  summary: { totalWOs: number; avgResolution: string; avgSLA: number; avgCapacity: number }
) {
  const trendLabel = (t: string) => (t === 'up' ? '▲ Up' : t === 'down' ? '▼ Down' : '— Stable');

  const memberRows = kpis
    .map((k) => {
      const m = members.find((tm) => tm.id === k.memberId);
      return `
        <tr>
          <td>${m?.name || ''}</td>
          <td>${m?.role || ''}</td>
          <td style="text-align:center;font-weight:700;color:#4f46e5">${k.wosCompleted}</td>
          <td style="text-align:center">${k.avgResolutionHrs}h</td>
          <td style="text-align:center;font-weight:700;color:${k.slaCompliance >= 95 ? '#16a34a' : k.slaCompliance >= 85 ? '#4f46e5' : '#d97706'}">${k.slaCompliance}%</td>
          <td style="text-align:center;font-weight:700;color:${k.capacityUtilization >= 85 ? '#dc2626' : k.capacityUtilization >= 70 ? '#d97706' : '#16a34a'}">${k.capacityUtilization}%</td>
          <td style="text-align:center;color:${k.trend === 'up' ? '#16a34a' : k.trend === 'down' ? '#dc2626' : '#6b7280'}">${trendLabel(k.trend)}</td>
        </tr>
      `;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Team Performance Report — ${month}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; padding: 40px; }
    .header { border-bottom: 3px solid #4f46e5; padding-bottom: 20px; margin-bottom: 28px; }
    .header h1 { font-size: 24px; color: #4f46e5; margin-bottom: 4px; }
    .header p { font-size: 13px; color: #64748b; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 28px; }
    .kpi-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; text-align: center; }
    .kpi-card .value { font-size: 28px; font-weight: 800; font-family: monospace; }
    .kpi-card .label { font-size: 11px; color: #64748b; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
    .section-title { font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #f8fafc; padding: 8px 12px; text-align: left; font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e2e8f0; }
    td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
    tr:last-child td { border-bottom: none; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>📊 Monthly Performance Report</h1>
    <p>NetOpsAI NOC Platform · Nablix Air · ${month}</p>
    <p style="margin-top:4px;font-size:12px;color:#94a3b8">Generated: ${new Date().toLocaleString()}</p>
  </div>

  <div class="kpi-grid">
    <div class="kpi-card">
      <div class="value" style="color:#4f46e5">${summary.totalWOs}</div>
      <div class="label">WOs Completed</div>
    </div>
    <div class="kpi-card">
      <div class="value" style="color:#0284c7">${summary.avgResolution}h</div>
      <div class="label">Avg Resolution</div>
    </div>
    <div class="kpi-card">
      <div class="value" style="color:#16a34a">${summary.avgSLA}%</div>
      <div class="label">SLA Compliance</div>
    </div>
    <div class="kpi-card">
      <div class="value" style="color:#d97706">${summary.avgCapacity}%</div>
      <div class="label">Capacity Utilization</div>
    </div>
  </div>

  <div class="section-title">Per-Member KPIs — ${month}</div>
  <table>
    <thead>
      <tr>
        <th>Member</th>
        <th>Role</th>
        <th style="text-align:center">WOs Done</th>
        <th style="text-align:center">Avg Res.</th>
        <th style="text-align:center">SLA %</th>
        <th style="text-align:center">Capacity</th>
        <th style="text-align:center">Trend</th>
      </tr>
    </thead>
    <tbody>
      ${memberRows}
    </tbody>
  </table>

  <div class="footer">NetOpsAI NOC Platform · Team Performance Report · ${month}</div>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
}

// ─── Email Schedule Modal ─────────────────────────────────────────────────────

interface EmailScheduleModalProps {
  onClose: () => void;
  kpis: MemberKPI[];
  members: TeamMember[];
  summary: { totalWOs: number; avgResolution: string; avgSLA: number; avgCapacity: number };
}

function EmailScheduleModal({ onClose, kpis, members, summary }: EmailScheduleModalProps) {
  const [recipientInput, setRecipientInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>(
    members.map((m) => m.email)
  );
  const [selectedMonth, setSelectedMonth] = useState('March 2026');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const months = ['September 2025', 'October 2025', 'November 2025', 'December 2025', 'January 2026', 'February 2026', 'March 2026'];

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !recipients.includes(email)) {
      setRecipients([...recipients, email]);
      setRecipientInput('');
    }
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter((r) => r !== email));
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      setError('Add at least one recipient.');
      return;
    }
    setSending(true);
    setError('');

    const memberReports = kpis.map((k) => {
      const m = members.find((tm) => tm.id === k.memberId);
      return {
        name: m?.name || '',
        role: m?.role || '',
        avatar: m?.avatar || '',
        wosCompleted: k.wosCompleted,
        avgResolutionHrs: k.avgResolutionHrs,
        slaCompliance: k.slaCompliance,
        capacityUtilization: k.capacityUtilization,
        trend: k.trend,
      };
    });

    const [month, year] = selectedMonth.split(' ');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-performance-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            recipients,
            month,
            year,
            teamSummary: summary,
            memberReports,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to send email');
      }
      setSent(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Send size={15} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Email Performance Summary</h2>
              <p className="text-[11px] text-muted">Send monthly report to team members</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} className="text-success" />
            </div>
            <h3 className="text-base font-bold text-foreground mb-1">Report Sent!</h3>
            <p className="text-xs text-muted mb-5">
              Performance summary for <span className="text-foreground font-semibold">{selectedMonth}</span> was sent to{' '}
              <span className="text-primary font-semibold">{recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</span>.
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 bg-primary/15 text-primary text-xs font-semibold rounded-lg hover:bg-primary/25 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Month selector */}
            <div>
              <label className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Calendar size={11} /> Report Period
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50"
              >
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Recipients */}
            <div>
              <label className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Mail size={11} /> Recipients ({recipients.length})
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="email"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                  placeholder="Add email address..."
                  className="flex-1 bg-surface-raised border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={addRecipient}
                  className="px-3 py-2 bg-primary/15 text-primary text-xs font-semibold rounded-lg hover:bg-primary/25 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                {recipients.map((email) => {
                  const member = members.find((m) => m.email === email);
                  return (
                    <div
                      key={email}
                      className="flex items-center gap-2 bg-surface-raised border border-border rounded-lg px-3 py-1.5"
                    >
                      {member && (
                        <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[9px] font-bold flex-shrink-0">
                          {member.avatar}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        {member && <p className="text-[11px] font-semibold text-foreground">{member.name}</p>}
                        <p className="text-[10px] font-mono text-muted truncate">{email}</p>
                      </div>
                      <button
                        onClick={() => removeRecipient(email)}
                        className="text-muted hover:text-danger transition-colors flex-shrink-0"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview note */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2.5 flex items-start gap-2">
              <FileText size={12} className="text-primary mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-muted">
                Email will include team summary KPIs and per-member breakdown for{' '}
                <span className="text-foreground font-semibold">{selectedMonth}</span>.
              </p>
            </div>

            {error && (
              <div className="bg-danger/10 border border-danger/20 rounded-lg px-3 py-2 text-[11px] text-danger">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={onClose}
                className="flex-1 py-2 text-xs font-semibold text-muted border border-border rounded-lg hover:bg-surface-raised transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || recipients.length === 0}
                className="flex-1 py-2 text-xs font-semibold bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {sending ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={12} />
                    Send Report
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Performance Components ───────────────────────────────────────────────────

function KpiPill({
  value,
  label,
  suffix = '',
  color = 'text-foreground',
}: {
  value: number | string;
  label: string;
  suffix?: string;
  color?: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={`text-sm font-bold font-mono-data ${color}`}>
        {value}{suffix}
      </span>
      <span className="text-[10px] text-muted mt-0.5 whitespace-nowrap">{label}</span>
    </div>
  );
}

function UtilBar({ value }: { value: number }) {
  const color =
    value >= 85 ? 'bg-danger' : value >= 70 ? 'bg-warning' : 'bg-success';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-surface-raised rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-[11px] font-mono-data text-foreground-muted w-8 text-right">{value}%</span>
    </div>
  );
}

function SlaBar({ value }: { value: number }) {
  const color =
    value >= 95 ? 'bg-success' : value >= 85 ? 'bg-primary' : 'bg-warning';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-surface-raised rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-[11px] font-mono-data text-foreground-muted w-8 text-right">{value}%</span>
    </div>
  );
}

function TrendBadge({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  if (trend === 'up') return (
    <span className="flex items-center gap-0.5 text-[10px] font-semibold text-success bg-success/10 px-1.5 py-0.5 rounded-full">
      <TrendingUp size={9} /> Up
    </span>
  );
  if (trend === 'down') return (
    <span className="flex items-center gap-0.5 text-[10px] font-semibold text-danger bg-danger/10 px-1.5 py-0.5 rounded-full">
      <TrendingUp size={9} className="rotate-180" /> Down
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-[10px] font-semibold text-muted bg-surface-raised px-1.5 py-0.5 rounded-full border border-border">
      — Stable
    </span>
  );
}

function PerformanceDashboard() {
  const [trendMetric, setTrendMetric] = useState<'wos' | 'sla'>('wos');
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [slaNodeFilter, setSlaNodeFilter] = useState<NodeTypeFilter>('all');
  const [slaSegmentFilter, setSlaSegmentFilter] = useState<SegmentFilter>('all');

  // Team-level summary stats
  const totalWOs = memberKPIs.reduce((s, k) => s + k.wosCompleted, 0);
  const avgResolution = (memberKPIs.reduce((s, k) => s + k.avgResolutionHrs, 0) / memberKPIs.length).toFixed(1);
  const avgSLA = Math.round(memberKPIs.reduce((s, k) => s + k.slaCompliance, 0) / memberKPIs.length);
  const avgCapacity = Math.round(memberKPIs.reduce((s, k) => s + k.capacityUtilization, 0) / memberKPIs.length);

  const summary = { totalWOs, avgResolution, avgSLA, avgCapacity };

  const trendData = trendMetric === 'wos' ? monthlyTrends : slaMonthlyTrends;

  // Filter visible members for chart
  const visibleAvatars = selectedMember
    ? [selectedMember]
    : teamMembers.map((m) => m.avatar);

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Team Summary KPI Strip ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            icon: CheckCircle2,
            label: 'WOs Completed',
            value: totalWOs,
            suffix: '',
            sub: 'This month · all members',
            color: 'text-primary',
            bg: 'bg-primary/10 border-primary/20',
          },
          {
            icon: Timer,
            label: 'Avg Resolution',
            value: avgResolution,
            suffix: 'h',
            sub: 'Mean time to resolve',
            color: 'text-info',
            bg: 'bg-info/10 border-info/20',
          },
          {
            icon: Shield,
            label: 'SLA Compliance',
            value: avgSLA,
            suffix: '%',
            sub: 'Team average',
            color: 'text-success',
            bg: 'bg-success/10 border-success/20',
          },
          {
            icon: Activity,
            label: 'Capacity Utilization',
            value: avgCapacity,
            suffix: '%',
            sub: 'Avg across team',
            color: 'text-warning',
            bg: 'bg-warning/10 border-warning/20',
          },
        ].map((kpi) => {
          const KpiIcon = kpi.icon;
          return (
            <div
              key={kpi.label}
              className={`bg-surface border rounded-xl p-4 flex items-start gap-3 ${kpi.bg}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${kpi.bg}`}>
                <KpiIcon size={16} className={kpi.color} />
              </div>
              <div className="min-w-0">
                <p className={`text-xl font-bold font-mono-data ${kpi.color}`}>
                  {kpi.value}{kpi.suffix}
                </p>
                <p className="text-xs font-semibold text-foreground mt-0.5">{kpi.label}</p>
                <p className="text-[10px] text-muted mt-0.5">{kpi.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Per-Member KPI Table ── */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface-raised/40">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Per-Member KPIs</h3>
            <span className="text-[10px] text-muted bg-surface-raised border border-border px-2 py-0.5 rounded-full">
              March 2026
            </span>
          </div>
          {/* Export actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportCSV(memberKPIs, teamMembers, 'March 2026')}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-success bg-success/10 border border-success/20 px-2.5 py-1.5 rounded-lg hover:bg-success/20 transition-colors"
            >
              <Download size={12} />
              CSV
            </button>
            <button
              onClick={() => generatePDFReport(memberKPIs, teamMembers, 'March 2026', summary)}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-warning bg-warning/10 border border-warning/20 px-2.5 py-1.5 rounded-lg hover:bg-warning/20 transition-colors"
            >
              <FileText size={12} />
              PDF
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2.5 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
            >
              <Send size={12} />
              Email
            </button>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[2fr_1fr_1fr_2fr_2fr_1fr] gap-0 border-b border-border bg-surface-raised/20 px-5 py-2">
          {['Member', 'WOs Done', 'Avg Res.', 'SLA Compliance', 'Capacity', 'Trend'].map((h) => (
            <div key={h} className="text-[10px] font-semibold text-muted uppercase tracking-wider">
              {h}
            </div>
          ))}
        </div>

        {/* Rows */}
        {memberKPIs.map((kpi) => {
          const member = teamMembers.find((m) => m.id === kpi.memberId);
          if (!member) return null;
          const avatarKey = member.avatar;
          const dotColor = MEMBER_COLORS[avatarKey] || '#94a3b8';
          return (
            <div
              key={kpi.memberId}
              className="grid grid-cols-[2fr_1fr_1fr_2fr_2fr_1fr] gap-0 px-5 py-3 border-b border-border last:border-0 hover:bg-surface-raised/30 transition-colors"
            >
              {/* Member */}
              <div className="flex items-center gap-2.5">
                <div
                  className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                  style={{ backgroundColor: `${dotColor}22`, color: dotColor, border: `1.5px solid ${dotColor}55` }}
                >
                  {member.avatar}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{member.name}</p>
                  <p className="text-[10px] text-muted truncate">{member.role}</p>
                </div>
              </div>

              {/* WOs Completed */}
              <div className="flex items-center">
                <span className="text-sm font-bold font-mono-data text-primary">{kpi.wosCompleted}</span>
              </div>

              {/* Avg Resolution */}
              <div className="flex items-center">
                <span className="text-xs font-mono-data text-foreground-muted">{kpi.avgResolutionHrs}h</span>
              </div>

              {/* SLA Compliance */}
              <div className="flex items-center pr-4">
                <div className="w-full">
                  <SlaBar value={kpi.slaCompliance} />
                </div>
              </div>

              {/* Capacity */}
              <div className="flex items-center pr-4">
                <div className="w-full">
                  <UtilBar value={kpi.capacityUtilization} />
                </div>
              </div>

              {/* Trend */}
              <div className="flex items-center">
                <TrendBadge trend={kpi.trend} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Monthly Trends Chart ── */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface-raised/40">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Monthly Trends</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Metric toggle */}
            <div className="flex items-center gap-1 bg-surface-raised border border-border rounded-lg p-0.5">
              <button
                onClick={() => setTrendMetric('wos')}
                className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-all ${
                  trendMetric === 'wos' ? 'bg-primary/15 text-primary' : 'text-muted hover:text-foreground'
                }`}
              >
                WOs Completed
              </button>
              <button
                onClick={() => setTrendMetric('sla')}
                className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-all ${
                  trendMetric === 'sla' ? 'bg-primary/15 text-primary' : 'text-muted hover:text-foreground'
                }`}
              >
                SLA %
              </button>
            </div>
          </div>
        </div>

        {/* Member filter pills */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border flex-wrap">
          <button
            onClick={() => setSelectedMember(null)}
            className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border transition-all ${
              selectedMember === null
                ? 'bg-primary/15 text-primary border-primary/30' :'text-muted border-border hover:bg-surface-raised'
            }`}
          >
            All Members
          </button>
          {teamMembers.map((m) => {
            const color = MEMBER_COLORS[m.avatar] || '#94a3b8';
            return (
              <button
                key={m.id}
                onClick={() => setSelectedMember(selectedMember === m.avatar ? null : m.avatar)}
                className={`flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full font-semibold border transition-all ${
                  selectedMember === m.avatar
                    ? 'border-current' :'border-border hover:bg-surface-raised'
                }`}
                style={
                  selectedMember === m.avatar
                    ? { color, backgroundColor: `${color}18`, borderColor: `${color}55` }
                    : {}
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                {m.avatar}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 20%)" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                axisLine={false}
                tickLine={false}
                domain={trendMetric === 'sla' ? [75, 100] : ['auto', 'auto']}
                tickFormatter={(v) => trendMetric === 'sla' ? `${v}%` : String(v)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(222 18% 10%)',
                  border: '1px solid hsl(222 14% 20%)',
                  borderRadius: '8px',
                  fontSize: 11,
                }}
                labelStyle={{ color: 'hsl(210 20% 92%)', fontWeight: 600 }}
                itemStyle={{ color: 'hsl(210 10% 65%)' }}
                formatter={(value: number, name: string) => {
                  const member = teamMembers.find((m) => m.avatar === name);
                  return [
                    trendMetric === 'sla' ? `${value}%` : value,
                    member?.name || name,
                  ];
                }}
              />
              {teamMembers
                .filter((m) => visibleAvatars.includes(m.avatar))
                .map((m) => (
                  <Line
                    key={m.avatar}
                    type="monotone"
                    dataKey={m.avatar}
                    stroke={MEMBER_COLORS[m.avatar] || '#94a3b8'}
                    strokeWidth={2}
                    dot={{ r: 3, fill: MEMBER_COLORS[m.avatar] || '#94a3b8', strokeWidth: 0 }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Capacity Utilization Bar Chart ── */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-surface-raised/40">
          <Zap size={14} className="text-warning" />
          <h3 className="text-sm font-semibold text-foreground">Capacity Utilization by Member</h3>
          <span className="ml-auto text-[10px] text-muted">
            <span className="inline-block w-2 h-2 rounded-sm bg-success mr-1" />{'<70%'}
            <span className="inline-block w-2 h-2 rounded-sm bg-warning mx-1 ml-2" />70–84%
            <span className="inline-block w-2 h-2 rounded-sm bg-danger mx-1 ml-2" />{'≥85%'}
          </span>
        </div>
        <div className="p-5">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={memberKPIs.map((k) => {
                const m = teamMembers.find((tm) => tm.id === k.memberId);
                return { name: m?.avatar || '', fullName: m?.name || '', value: k.capacityUtilization };
              })}
              margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 20%)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(222 18% 10%)',
                  border: '1px solid hsl(222 14% 20%)',
                  borderRadius: '8px',
                  fontSize: 11,
                }}
                labelStyle={{ color: 'hsl(210 20% 92%)', fontWeight: 600 }}
                formatter={(value: number, _: string, props: { payload?: { fullName?: string } }) => [
                  `${value}%`,
                  props?.payload?.fullName || 'Utilization',
                ]}
              />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                fill="hsl(189 94% 43%)"
                // Color each bar individually via cell
                label={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── SLA Trends by Node Type & Customer Segment ── */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface-raised/40">
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-success" />
            <h3 className="text-sm font-semibold text-foreground">SLA Trends by Node Type &amp; Customer Segment</h3>
          </div>
          <span className="text-[10px] text-muted bg-surface-raised border border-border px-2 py-0.5 rounded-full">
            Sep 2025 – Mar 2026
          </span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-b border-border bg-surface-raised/10">
          {/* Node Type filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-muted uppercase tracking-wider mr-1">Node Type</span>
            {(['all', 'BN', 'RN'] as NodeTypeFilter[]).map((v) => (
              <button
                key={v}
                onClick={() => setSlaNodeFilter(v)}
                className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold border transition-all ${
                  slaNodeFilter === v
                    ? 'bg-primary/15 text-primary border-primary/30' :'text-muted border-border hover:bg-surface-raised'
                }`}
              >
                {v === 'all' ? 'All' : v === 'BN' ? 'BN (Base Node)' : 'RN (Remote Node)'}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-border" />

          {/* Segment filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-muted uppercase tracking-wider mr-1">Segment</span>
            {(['all', 'MDU', 'business', 'single-dwelling'] as SegmentFilter[]).map((v) => (
              <button
                key={v}
                onClick={() => setSlaSegmentFilter(v)}
                className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold border transition-all ${
                  slaSegmentFilter === v
                    ? 'bg-success/15 text-success border-success/30' :'text-muted border-border hover:bg-surface-raised'
                }`}
              >
                {v === 'all' ? 'All' : v === 'MDU' ? 'MDU' : v === 'business' ? 'Business' : 'Single-Dwelling'}
              </button>
            ))}
          </div>
        </div>

        {/* Legend pills */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-border">
          {SLA_SERIES.filter((s) => {
            const nodeOk = slaNodeFilter === 'all' || s.nodeType === slaNodeFilter;
            const segOk = slaSegmentFilter === 'all' || s.segment === slaSegmentFilter;
            return nodeOk && segOk;
          }).map((s) => (
            <span
              key={s.key}
              className="flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border"
              style={{ color: s.color, backgroundColor: `${s.color}18`, borderColor: `${s.color}44` }}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
        </div>

        <div className="p-5">
          {(() => {
            const visibleSeries = SLA_SERIES.filter((s) => {
              const nodeOk = slaNodeFilter === 'all' || s.nodeType === slaNodeFilter;
              const segOk = slaSegmentFilter === 'all' || s.segment === slaSegmentFilter;
              return nodeOk && segOk;
            });

            if (visibleSeries.length === 0) {
              return (
                <div className="flex items-center justify-center h-40 text-muted text-xs">
                  No series match the selected filters.
                </div>
              );
            }

            return (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={slaTrendsBySegment} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 20%)" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(222 10% 45%)' }}
                    axisLine={false}
                    tickLine={false}
                    domain={[75, 100]}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222 18% 10%)',
                      border: '1px solid hsl(222 14% 20%)',
                      borderRadius: '8px',
                      fontSize: 11,
                    }}
                    labelStyle={{ color: 'hsl(210 20% 92%)', fontWeight: 600 }}
                    itemStyle={{ color: 'hsl(210 10% 65%)' }}
                    formatter={(value: number, name: string) => {
                      const series = SLA_SERIES.find((s) => s.key === name);
                      return [`${value}%`, series?.label || name];
                    }}
                  />
                  {visibleSeries.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: s.color, strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 0 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </div>

        {/* Insight strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 border-t border-border">
          {[
            { label: 'BN Avg SLA', value: '95.3%', sub: 'Base nodes — Mar 2026', color: 'text-primary' },
            { label: 'RN Avg SLA', value: '91.0%', sub: 'Remote nodes — Mar 2026', color: 'text-info' },
            { label: 'MDU Gap (BN–RN)', value: '8 pp', sub: 'MDU SLA spread this month', color: 'text-warning' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`px-5 py-3 flex flex-col gap-0.5 ${i < 2 ? 'border-r border-border' : ''}`}
            >
              <span className={`text-base font-bold font-mono-data ${stat.color}`}>{stat.value}</span>
              <span className="text-[11px] font-semibold text-foreground">{stat.label}</span>
              <span className="text-[10px] text-muted">{stat.sub}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <EmailScheduleModal
          onClose={() => setShowEmailModal(false)}
          kpis={memberKPIs}
          members={teamMembers}
          summary={summary}
        />
      )}

    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'members' | 'roles' | 'oncall' | 'escalation' | 'performance';

export default function TeamPage() {
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [deptFilter, setDeptFilter] = useState<string>('All');
  const [shiftView, setShiftView] = useState<'day' | 'night'>('day');

  const departments = ['All', 'Network', 'Infrastructure', 'Security', 'Management'];

  const filteredMembers =
    deptFilter === 'All' ? teamMembers : teamMembers.filter((m) => m.department === deptFilter);

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'members', label: 'Team Members', icon: Users },
    { id: 'roles', label: 'Department Roles', icon: Shield },
    { id: 'oncall', label: 'On-Call Schedules', icon: CalendarDays },
    { id: 'escalation', label: 'Escalation Rules', icon: AlertTriangle },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
  ];

  const onlineCount = teamMembers.filter((m) => m.status === 'online').length;

  // Group escalation rules by name
  const escalationGroups = escalationRules.reduce<Record<string, EscalationRule[]>>((acc, rule) => {
    if (!acc[rule.name]) acc[rule.name] = [];
    acc[rule.name].push(rule);
    return acc;
  }, {});

  return (
    <AppLayout>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Page Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-surface">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">Team Management</h1>
              <p className="text-xs text-muted mt-0.5">Nablix Air · Addis Ababa, Ethiopia</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-success font-medium bg-success/10 border border-success/20 px-3 py-1.5 rounded-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                {onlineCount} Online
              </div>
              <div className="text-xs text-muted bg-surface-raised border border-border px-3 py-1.5 rounded-lg">
                {teamMembers.length} Members
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 flex-wrap">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
                    ${activeTab === tab.id
                      ? 'bg-primary/15 text-primary' :'text-foreground-muted hover:bg-surface-raised hover:text-foreground'
                    }`}
                >
                  <TabIcon size={13} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Team Members Tab ── */}
          {activeTab === 'members' && (
            <div>
              {/* Filter */}
              <div className="flex items-center gap-2 mb-5">
                {departments.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDeptFilter(d)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-all duration-150
                      ${deptFilter === d
                        ? 'bg-primary/15 text-primary border-primary/30' :'text-muted border-border hover:bg-surface-raised'}`}
                  >
                    {d}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredMembers.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          )}

          {/* ── Department Roles Tab ── */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              {departmentRoles.map((dept) => (
                <div key={dept.dept} className="bg-surface border border-border rounded-xl overflow-hidden">
                  <div className={`flex items-center gap-2 px-5 py-3 border-b border-border`}>
                    <Shield size={15} className={dept.color.split(' ')[0]} />
                    <h3 className="text-sm font-semibold text-foreground">{dept.dept} Department</h3>
                    <span className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full border ${dept.color}`}>
                      {teamMembers.filter((m) => m.department === dept.dept).length} members
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {dept.roles.map((role) => {
                        const member = teamMembers.find((m) => m.role === role);
                        return (
                          <div key={role} className="flex items-center gap-3 bg-surface-raised rounded-lg px-3 py-2.5 border border-border">
                            <UserCheck size={14} className="text-muted flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-foreground">{role}</p>
                              {member && (
                                <p className="text-[11px] text-muted truncate">{member.name}</p>
                              )}
                            </div>
                            {member && (
                              <span className={`ml-auto w-2 h-2 rounded-full flex-shrink-0 ${statusDot[member.status]}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Members in dept */}
                    <div className="space-y-2">
                      {teamMembers
                        .filter((m) => m.department === dept.dept)
                        .map((m) => (
                          <div key={m.id} className="flex items-center gap-3 text-xs">
                            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-[10px] font-bold flex-shrink-0">
                              {m.avatar}
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-foreground">{m.name}</span>
                              <span className="text-muted ml-2">{m.role}</span>
                            </div>
                            <span className="text-[11px] font-mono text-muted">{m.email}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── On-Call Schedules Tab ── */}
          {activeTab === 'oncall' && (
            <div>
              {/* Shift toggle */}
              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => setShiftView('day')}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border transition-all
                    ${shiftView === 'day' ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted border-border hover:bg-surface-raised'}`}
                >
                  <Clock size={12} /> Day Shift (08:00–20:00)
                </button>
                <button
                  onClick={() => setShiftView('night')}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border transition-all
                    ${shiftView === 'night' ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted border-border hover:bg-surface-raised'}`}
                >
                  <Clock size={12} /> Night Shift (20:00–08:00)
                </button>
              </div>

              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="grid grid-cols-4 gap-0 border-b border-border bg-surface-raised">
                  {['Day', 'Primary On-Call', 'Secondary On-Call', 'Shift Hours'].map((h) => (
                    <div key={h} className="px-4 py-2.5 text-[11px] font-semibold text-muted uppercase tracking-wider">
                      {h}
                    </div>
                  ))}
                </div>
                {(shiftView === 'day' ? onCallSchedule : nightShift).map((row, idx) => {
                  const primaryMember = teamMembers.find((m) => m.name === row.primary);
                  const secondaryMember = teamMembers.find((m) => m.name === row.secondary);
                  const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }) === row.day;
                  return (
                    <div
                      key={row.day}
                      className={`grid grid-cols-4 gap-0 border-b border-border last:border-0 transition-colors
                        ${isToday ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-surface' : 'bg-surface-raised/30'}`}
                    >
                      <div className="px-4 py-3 flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{row.day}</span>
                        {isToday && (
                          <span className="text-[9px] font-bold text-primary bg-primary/15 px-1.5 py-0.5 rounded-full">TODAY</span>
                        )}
                      </div>
                      <div className="px-4 py-3 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px] font-bold flex-shrink-0">
                          {primaryMember?.avatar}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-foreground">{row.primary}</p>
                          <p className="text-[10px] text-muted">{primaryMember?.role}</p>
                        </div>
                      </div>
                      <div className="px-4 py-3 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-surface-raised border border-border flex items-center justify-center text-muted text-[10px] font-bold flex-shrink-0">
                          {secondaryMember?.avatar}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-foreground-muted">{row.secondary}</p>
                          <p className="text-[10px] text-muted">{secondaryMember?.role}</p>
                        </div>
                      </div>
                      <div className="px-4 py-3 flex items-center">
                        <span className="text-xs font-mono text-muted">{row.shift}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex items-center gap-4 text-[11px] text-muted">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-primary/20 border border-primary/30" />
                  Primary — first responder
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-surface-raised border border-border" />
                  Secondary — backup responder
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-primary/5 border border-primary/20" />
                  Today's shift
                </div>
              </div>
            </div>
          )}

          {/* ── Escalation Rules Tab ── */}
          {activeTab === 'escalation' && (
            <div className="space-y-5">
              {Object.entries(escalationGroups).map(([ruleName, rules]) => (
                <div key={ruleName} className="bg-surface border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-surface-raised/50">
                    <AlertTriangle size={14} className="text-warning" />
                    <h3 className="text-sm font-semibold text-foreground">{ruleName}</h3>
                    <span className="ml-2 text-[11px] text-muted bg-surface-raised border border-border px-2 py-0.5 rounded-full">
                      {rules[0].trigger}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start gap-0">
                      {rules
                        .sort((a, b) => a.level - b.level)
                        .map((rule, idx) => {
                          const member = teamMembers.find((m) => m.name === rule.assignee);
                          return (
                            <React.Fragment key={rule.id}>
                              <div className="flex flex-col items-center min-w-[180px]">
                                {/* Level badge */}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 mb-2
                                  ${rule.level === 1
                                    ? 'bg-primary/20 border-primary text-primary' :'bg-warning/20 border-warning text-warning'
                                  }`}>
                                  L{rule.level}
                                </div>
                                <div className="bg-surface-raised border border-border rounded-lg p-3 w-full text-center">
                                  <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold mx-auto mb-1.5">
                                    {member?.avatar}
                                  </div>
                                  <p className="text-xs font-semibold text-foreground">{rule.assignee}</p>
                                  <p className="text-[10px] text-muted mt-0.5">{member?.role}</p>
                                  <div className="mt-2 pt-2 border-t border-border space-y-1">
                                    <div className="flex items-center justify-center gap-1 text-[10px] text-muted">
                                      <Clock size={9} />
                                      Wait {rule.waitMinutes} min
                                    </div>
                                    <div className="text-[10px] font-medium text-primary">{rule.channel}</div>
                                  </div>
                                </div>
                              </div>
                              {idx < rules.length - 1 && (
                                <div className="flex items-center mt-10 mx-2">
                                  <ArrowRight size={16} className="text-muted" />
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Info note */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-start gap-3">
                <Shield size={14} className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Escalation Policy</p>
                  <p className="text-[11px] text-muted mt-0.5">
                    If the Level 1 assignee does not acknowledge within the wait time, the alert automatically escalates to Level 2.
                    Tigist Worku (Manager) is the final escalation point for all critical incidents.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Performance Tab ── */}
          {activeTab === 'performance' && <PerformanceDashboard />}

        </div>
      </div>
    </AppLayout>
  );
}
